import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaBolt, FaGasPump, FaIndustry, FaChartLine } from 'react-icons/fa';
import PageHeader from '../../components/common/PageHeader';

const EnergiesFundPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Fund', path: '/fund' },
    { label: 'Energies' }
  ];

  const keyFeatures = [
    {
      icon: <FaBolt className="w-10 h-10 text-secondary" />,
      title: "Energy Innovation",
      description: "We invest in innovative energy technologies and companies that are transforming the traditional energy landscape."
    },
    {
      icon: <FaGasPump className="w-10 h-10 text-secondary" />,
      title: "Diversified Portfolio",
      description: "Our fund provides exposure to a diverse range of energy sectors, including oil, gas, renewables, and emerging technologies."
    },
    {
      icon: <FaIndustry className="w-10 h-10 text-secondary" />,
      title: "Industry Expertise",
      description: "Our team combines deep industry knowledge with financial expertise to identify the most promising energy investment opportunities."
    },
    {
      icon: <FaChartLine className="w-10 h-10 text-secondary" />,
      title: "Long-term Growth",
      description: "We focus on long-term growth potential in the energy sector, balancing traditional energy investments with emerging technologies."
    }
  ];

  const stats = [
    { value: "$350M", label: "Assets Under Management" },
    { value: "42+", label: "Energy Companies" },
    { value: "22%", label: "Average Annual Return" },
    { value: "2016", label: "Fund Established" }
  ];

  return (
    <>
      <PageHeader 
        title="Energies Fund" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-heading font-bold text-primary">Powering the Future of Energy Investment</h2>

              <p className="text-lg text-gray-700">
                The Blockfirst Capital Energies Fund offers investors strategic exposure to the global energy market, combining traditional energy investments with emerging technologies and renewable energy solutions.
              </p>

              <p className="text-lg text-gray-700">
                Our experienced team of energy industry experts and financial analysts identifies high-potential opportunities across the energy value chain, from production and distribution to innovative technologies and renewable solutions.
              </p>

              <div className="grid grid-cols-2 gap-4 pt-4">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center bg-white p-6 rounded-lg shadow-sm">
                    <div className="text-3xl font-heading font-bold text-secondary mb-2">{stat.value}</div>
                    <div className="text-gray-600 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/finance-money-debt-credit-balance-concept_53876-127372.jpg_rkd2ew.jpg" 
                alt="Energies Fund" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-heading font-bold mb-6">Fund Features</h2>
            <p className="text-lg text-gray-300">
              Our Energies Fund is designed to provide investors with comprehensive exposure to the energy sector, balancing traditional energy investments with emerging technologies and sustainable solutions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyFeatures.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:bg-white/15 transition-all duration-300"
              >
                <div className="mb-6">{feature.icon}</div>
                <h3 className="text-2xl font-heading font-semibold mb-4">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <img 
                src="/assets/person-looking-finance-graphs_52683-116611.jpg_mhsxez.jpg" 
                alt="Investment Strategy" 
                className="rounded-xl shadow-custom w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2 space-y-6"
            >
              <h2 className="text-4xl font-heading font-bold text-primary">Investment Strategy</h2>

              <p className="text-lg text-gray-700">
                Our investment strategy in the energy sector focuses on identifying companies and projects that demonstrate:
              </p>

              <div className="space-y-4">
                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-secondary">
                  <h3 className="text-xl font-heading font-semibold mb-2 text-primary">Traditional Energy Excellence</h3>
                  <p className="text-gray-600">
                    We invest in well-established energy companies with strong operational track records, solid cash flows, and sustainable dividend policies.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-secondary">
                  <h3 className="text-xl font-heading font-semibold mb-2 text-primary">Renewable Energy Innovation</h3>
                  <p className="text-gray-600">
                    We identify companies at the forefront of renewable energy technologies, including solar, wind, hydroelectric, and emerging alternative energy solutions.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-secondary">
                  <h3 className="text-xl font-heading font-semibold mb-2 text-primary">Energy Infrastructure</h3>
                  <p className="text-gray-600">
                    We invest in critical energy infrastructure assets that provide essential services and generate stable, long-term cash flows.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-heading font-bold text-primary mb-6">How to Invest</h2>
            <p className="text-lg text-gray-600">
              Investing in our Energies Fund is a straightforward process designed to provide you with full transparency and confidence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-custom p-8 text-center"
            >
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">1</div>
              <h3 className="text-2xl font-heading font-semibold mb-4 text-primary">Schedule a Consultation</h3>
              <p className="text-gray-600">
                Contact our team to schedule a personalized consultation with one of our energy investment specialists to discuss your financial goals.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-custom p-8 text-center"
            >
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">2</div>
              <h3 className="text-2xl font-heading font-semibold mb-4 text-primary">Review Fund Documents</h3>
              <p className="text-gray-600">
                Our team will provide you with detailed fund documents, including the investment strategy, energy sector analysis, risk factors, and performance history.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-custom p-8 text-center"
            >
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">3</div>
              <h3 className="text-2xl font-heading font-semibold mb-4 text-primary">Complete Subscription</h3>
              <p className="text-gray-600">
                Once you're ready to invest, complete the subscription agreement and transfer your investment to begin your energy investment journey.
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <a 
              href="/contact-us" 
              className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
            >
              Request Investment Information
            </a>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default EnergiesFundPage;